import React, { useState, useEffect } from 'react';
import { Card, Button, DataTable, Modal } from '../components/common/UIComponents';
import api from '../api/axios';

export default function Prizes() {
  const [prizes, setPrizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPrize, setEditingPrize] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_url: '',
    points_required: 50,
    stock: 10,
    status: 'active'
  });

  const fetchPrizes = async () => {
    try {
      const res = await api.get('/prizes');
      setPrizes(res.data);
    } catch (err) {
      console.error('Error al cargar premios:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrizes();
  }, []);

  const handleOpenModal = (prize = null) => {
    if (prize) {
      setEditingPrize(prize);
      setFormData({
        name: prize.name,
        description: prize.description || '',
        image_url: prize.image_url || '',
        points_required: prize.points_required,
        stock: prize.stock,
        status: prize.status || 'active'
      });
    } else {
      setEditingPrize(null);
      setFormData({
        name: '',
        description: '',
        image_url: '',
        points_required: 50,
        stock: 10,
        status: 'active'
      });
    }
    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editingPrize) {
        await api.put(`/prizes/${editingPrize.id}`, formData);
      } else {
        await api.post('/prizes', formData);
      }
      setIsModalOpen(false);
      fetchPrizes();
    } catch (err) {
      console.error('Error al guardar premio:', err);
      alert(err.response?.data?.message || 'Error al guardar el premio.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de que desea eliminar este premio?')) return;
    try {
      await api.delete(`/prizes/${id}`);
      fetchPrizes();
    } catch (err) {
      console.error('Error al eliminar premio:', err);
      alert(err.response?.data?.message || 'Error al eliminar el premio.');
    }
  };

  const columns = [
    {
      header: 'Premio',
      render: (row) => (
        <div className="flex items-center gap-3">
          {row.image_url ? (
            <img src={row.image_url} alt={row.name} className="w-10 h-10 rounded-lg object-cover border border-gray-200" />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center font-bold text-lg">
              🎁
            </div>
          )}
          <div>
            <p className="font-bold text-gray-800">{row.name}</p>
            <p className="text-xs text-gray-400 line-clamp-1">{row.description}</p>
          </div>
        </div>
      )
    },
    { header: 'Puntos Requeridos', render: (row) => <span className="font-bold text-purple-600">{row.points_required} pts</span> },
    { header: 'Stock Disponible', render: (row) => <span className="font-semibold text-gray-700">{row.stock} unids</span> },
    {
      header: 'Estado',
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${row.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
          {row.status === 'active' ? 'Activo' : 'Inactivo'}
        </span>
      )
    },
    {
      header: 'Acciones',
      render: (row) => (
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => handleOpenModal(row)}>
            Editar
          </Button>
          <Button size="sm" variant="danger" onClick={() => handleDelete(row.id)}>
            Eliminar
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Catálogo y Gestión de Premios</h1>
          <p className="text-sm text-gray-500">Gestione los premios disponibles para canje por puntos en el evento.</p>
        </div>
        <Button onClick={() => handleOpenModal()}>
          + Registrar Nuevo Premio
        </Button>
      </div>

      <Card>
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando catálogo de premios...</div>
        ) : (
          <DataTable columns={columns} data={prizes} emptyMessage="No hay premios registrados aún." />
        )}
      </Card>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingPrize ? 'Editar Premio' : 'Registrar Nuevo Premio'}
      >
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Nombre del Premio</label>
            <input
              type="text"
              required
              className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ej: Kit de Bienvenida Premium"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Descripción</label>
            <textarea
              className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none"
              rows="2"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descripción del premio o condiciones de entrega..."
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">URL Imagen del Premio</label>
            <input
              type="url"
              className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none"
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              placeholder="https://ejemplo.com/imagen.jpg"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Puntos Requeridos</label>
              <input
                type="number"
                min="0"
                required
                className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                value={formData.points_required}
                onChange={(e) => setFormData({ ...formData, points_required: parseInt(e.target.value, 10) })}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Stock Disponible</label>
              <input
                type="number"
                min="0"
                required
                className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                value={formData.stock}
                onChange={(e) => setFormData({ ...formData, stock: parseInt(e.target.value, 10) })}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Estado</label>
            <select
              className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="active">Activo</option>
              <option value="inactive">Inactivo</option>
            </select>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit">
              Guardar Premio
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
